import { useEffect } from 'react';
import type { To } from 'react-router';
import { useNavigate } from 'react-router';
import { ApprovalRequestStatusEnum } from '@gonfalon/approval-requests/types';
import { enableApprovalRequestClientSidePolling, fixPendingChanges429 } from '@gonfalon/dogfood-flags/dogfood-flags';
import { flashMessage } from '@gonfalon/flash-messages/flashMessage';
import { pluralize } from '@gonfalon/format/pluralize';
import { isImmutable } from '@gonfalon/immutable-shim/immutableShim';
import type { ImmutableServerError } from '@gonfalon/legacy-http-client/httpUtils';
import {
  isImmutableNoStatusError,
  isImmutableNotFoundError,
  isImmutableStandardForbiddenError,
  isUnexpectedResponseError,
} from '@gonfalon/legacy-http-client/httpUtils';
import { msg } from '@gonfalon/messages/msg';
import { toApprovals, toFlagTargeting } from '@gonfalon/navigator/navigators';
import { useParam } from '@gonfalon/router/useParam';
import { snackbarQueue, toastQueue } from '@launchpad-ui/components';
import type { QueryState, UseQueryOptions } from '@tanstack/react-query';
import { keepPreviousData, useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  requestApplyApprovalRequestDone,
  requestApplyApprovalRequestFailed,
  requestApprovalRequestFailed,
  requestApprovalRequestReviewDone,
  requestApprovalRequestReviewFailed,
  requestDeleteApprovalRequestDone,
  requestDeleteApprovalRequestFailed,
} from '#actions/approvals';
import { forceFetchFlag } from '#actions/flags';
import { fetchScheduledChangesForFlag } from '#actions/scheduledChanges';
import { useDispatch } from '#hooks/useDispatch';
import {
  deleteApprovalRequest,
  deleteApprovalRequestForFlagConfiguration,
  getApprovalRequestById,
  getApprovalRequestByIdForFlagConfiguration,
  getApprovalRequestsForAccount,
  postApplyApprovalRequest,
  postApplyApprovalRequestForFlagConfiguration,
  postReviewApprovalRequest,
  postReviewApprovalRequestForFlagConfiguration,
  updateReviewersForApprovalRequest,
  updateReviewersForApprovalRequestForFlagConfiguration,
} from '#sources/Approvals';
import { type ApprovalsReviewKind, ApprovalWorkflowExecutionStatus } from '#utils/approvals/constants';
import { getApprovalAppliedNotificationMessage } from '#utils/approvals/getApprovalAppliedNotificationMessage';
import { getApprovalApplyFailedNotificationMessage } from '#utils/approvals/getApprovalApplyFailedNotificationMessage';
import { getApprovalErrorNotificationMessage } from '#utils/approvals/getApprovalErrorNotificationMessage';
import { getApprovalNotificationMessage } from '#utils/approvals/getApprovalNotificationMessage';
import type { ApprovalRequest } from '#utils/approvals/records/ApprovalRequest';
import type { ApprovalRequestSummariesListResponse, ApprovalRequestSummary } from '#utils/approvals/types';
import type { ApprovalRequestSummariesFilters } from '#utils/approvalsFilterUtils';
import { makeApprovalRequestDashboardQueryParams } from '#utils/approvalsFilterUtils';

const bailoutStatuses = [400, 403, 404];

export const approvalRequestQueryDefaultOptions = {
  refetchOnWindowFocus: false,
  retry(failureCount: number, error: ImmutableServerError) {
    if (failureCount > 0) {
      return false;
    }

    // Read per attempt rather than at module load, so the rollout applies without a reload.
    const nonRetryableStatuses = fixPendingChanges429() ? [...bailoutStatuses, 429] : bailoutStatuses;

    return isImmutable(error) && !nonRetryableStatuses.includes(error.get('status'));
  },
  retryDelay: 0,
};

const APPROVAL_REQUESTS = 'approval_requests';
const SUMMARIES = 'summaries';
const approvalRequestQueryKeys = {
  approvalRequests: [APPROVAL_REQUESTS],
  approvalRequestById: (approvalRequestId: string) => [APPROVAL_REQUESTS, approvalRequestId],
  approvalRequestSummaries: [APPROVAL_REQUESTS, SUMMARIES],
  approvalRequestSummariesList: (filters?: ApprovalRequestSummariesFilters) =>
    filters ? [APPROVAL_REQUESTS, SUMMARIES, 'list', filters] : [APPROVAL_REQUESTS, SUMMARIES, 'list'],
  approvalRequestSummaryById: (approvalRequestId: string) => [APPROVAL_REQUESTS, SUMMARIES, approvalRequestId],
} as const;

function getApprovalRequestSummaryExpandProps() {
  return { project: true, environments: true, resource: true } as const;
}

function getApprovalRequestRefetchInterval(queryState: QueryState<ApprovalRequest, ImmutableServerError>) {
  if (!enableApprovalRequestClientSidePolling()) {
    return false;
  }

  // Don't poll if query is not in success state
  if (queryState.status !== 'success') {
    return false;
  }

  const data = queryState.data;

  if (!data) {
    return false;
  }

  const isCompleted = data?.status && data?.status !== ApprovalWorkflowExecutionStatus.PENDING;

  if (isCompleted) {
    return false;
  }

  // 1 minute interval if not completed
  return 60000;
}

export function approvalRequestSummariesListQueryKey(options?: {
  filters?: ApprovalRequestSummariesFilters;
  limit?: number;
  expand?: boolean;
}) {
  const listKey = approvalRequestQueryKeys.approvalRequestSummariesList(options?.filters);
  const expandSummaries = options?.expand !== false;
  return options?.limit === undefined && expandSummaries
    ? listKey
    : [...listKey, { limit: options?.limit, expand: expandSummaries }];
}

export function useApprovalRequestSummariesList(options?: {
  filters?: ApprovalRequestSummariesFilters;
  onError?: () => void;
  onFetched?: () => void;
  enabled?: boolean;
  notifyOnError?: boolean;
  limit?: number;
  expand?: boolean;
  refetchInterval?: number | false;
}) {
  const dispatch = useDispatch();
  const expandSummaries = options?.expand !== false;
  const queryKey = approvalRequestSummariesListQueryKey(options);

  const query = useQuery<ApprovalRequestSummariesListResponse, ImmutableServerError>({
    enabled: options?.enabled ?? true,
    queryKey,
    queryFn: async (): Promise<ApprovalRequestSummariesListResponse> => {
      const filterParams = options?.filters ? makeApprovalRequestDashboardQueryParams(options.filters) : {};
      const limitParams = options?.limit !== undefined ? { limit: options.limit } : {};

      if (!expandSummaries) {
        const response = await getApprovalRequestsForAccount({
          params: {
            ...filterParams,
            ...limitParams,
          },
        });
        options?.onFetched?.();
        return {
          items: [],
          totalCount: typeof response.result.totalCount === 'number' ? response.result.totalCount : 0,
          links: response.result.links ?? { self: { href: '', type: 'application/json' } },
        };
      }

      const response = await getApprovalRequestsForAccount({
        params: {
          ...filterParams,
          ...limitParams,
          expand: getApprovalRequestSummaryExpandProps(),
        },
      });
      const { approvalRequests } = response.entities;
      const { items } = response.result;
      options?.onFetched?.();
      return {
        ...response.result,
        items: (approvalRequests && items && items.map((id) => approvalRequests[id])) || [],
      };
    },
    ...approvalRequestQueryDefaultOptions,
    placeholderData: expandSummaries ? keepPreviousData : undefined,
    staleTime: 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });

  const { isError, error } = query;

  useEffect(() => {
    if (isError) {
      if (options?.notifyOnError !== false) {
        dispatch({
          type: 'approvals/FETCH_APPROVAL_REQUEST_SUMMARIES_FAILED',
          error,
        });
      }
      options?.onError?.();
      return;
    }
  }, [isError]);

  return query;
}

export function useApprovalRequestByIdForFlagConfiguration(
  {
    projKey,
    envKey,
    flagKey,
    approvalRequestId,
  }: { projKey: string; envKey: string; flagKey: string; approvalRequestId: string },
  options: Omit<UseQueryOptions<ApprovalRequest, ImmutableServerError>, 'queryKey'> = {},
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const query = useQuery<ApprovalRequest, ImmutableServerError>({
    queryKey: approvalRequestQueryKeys.approvalRequestById(approvalRequestId),
    queryFn: () =>
      getApprovalRequestByIdForFlagConfiguration({
        projKey,
        envKey,
        flagKey,
        requestId: approvalRequestId,
      }),
    refetchIntervalInBackground: true,
    refetchInterval: ({ state }) => getApprovalRequestRefetchInterval(state),
    ...approvalRequestQueryDefaultOptions,
    ...options,
  });

  const { isError, error } = query;

  useEffect(() => {
    if (isError) {
      dispatch(requestApprovalRequestFailed(error, projKey, flagKey, envKey, approvalRequestId));
      // oxlint-disable-next-line typescript/no-floating-promises
      navigate(toFlagTargeting({ projectKey: projKey, flagKey, environmentKey: envKey }));
      return;
    }
  }, [isError]);

  return query;
}

export function getApprovalRequestLoadErrorMessage(error: unknown, approvalRequestId: string) {
  if (isImmutableNotFoundError(error)) {
    return msg('approvals.requestRedirect.notFound', { approvalRequestId });
  }
  if (isImmutableStandardForbiddenError(error)) {
    return msg('approvals.requestRedirect.forbidden');
  }
  if (
    isImmutableNoStatusError(error) ||
    (isImmutable(error) && (isUnexpectedResponseError(error) || error.get('status') >= 500))
  ) {
    return msg('approvals.requestRedirect.unreachable', { approvalRequestId });
  }
  return undefined;
}

export function useApprovalRequestSummaryById(
  { approvalRequestId }: { approvalRequestId: string },
  options: Omit<UseQueryOptions<ApprovalRequestSummary, ImmutableServerError>, 'queryKey'> = {},
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const projectKey = useParam('projectKey');

  const query = useQuery<ApprovalRequestSummary, ImmutableServerError>({
    ...getApprovalRequestSummaryByIdQueryOptions(approvalRequestId),
    refetchIntervalInBackground: true,
    refetchInterval: ({ state }) => getApprovalRequestRefetchInterval(state),
    ...options,
  });

  const { isError, error } = query;
  const hasApprovalRequest = query.data !== undefined;

  useEffect(() => {
    if (isError && !hasApprovalRequest) {
      const loadErrorMessage = getApprovalRequestLoadErrorMessage(error, approvalRequestId);
      if (loadErrorMessage) {
        flashMessage('approvals-error', loadErrorMessage);
      } else {
        dispatch({
          type: 'approvals/FETCH_APPROVAL_REQUEST_SUMMARY_BY_ID_FAILED',
          error,
          approvalRequestId,
        });
      }
      // oxlint-disable-next-line typescript/no-floating-promises
      navigate(toApprovals({ projectKey }), { replace: true });
      return;
    }
    if (isError) {
      snackbarQueue.add({ description: msg('approvals.requestRefreshError.body'), status: 'error' });
    }
  }, [isError]);
  return query;
}

export function getApprovalRequestSummaryByIdQueryOptions(approvalRequestId: string) {
  return {
    queryKey: approvalRequestQueryKeys.approvalRequestSummaryById(approvalRequestId),
    queryFn: async (): Promise<ApprovalRequestSummary> =>
      getApprovalRequestById({
        approvalRequestId,
        expand: getApprovalRequestSummaryExpandProps(),
      }),
    ...approvalRequestQueryDefaultOptions,
  };
}

export function useReviewApprovalRequestForFlagConfiguration(props: {
  approvalRequestId: string;
  kind: ApprovalsReviewKind;
}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<
    ApprovalRequest,
    ImmutableServerError,
    Omit<Parameters<typeof postReviewApprovalRequestForFlagConfiguration>[0], 'kind'>
  >({
    mutationFn: (args) => postReviewApprovalRequestForFlagConfiguration({ ...args, kind: props.kind }),
    mutationKey: [...approvalRequestQueryKeys.approvalRequestSummaryById(props.approvalRequestId), props.kind],
    onSuccess(approvalRequest, { projKey, envKey, flagKey }) {
      dispatch(
        requestApprovalRequestReviewDone(
          projKey,
          envKey,
          flagKey,
          approvalRequest,
          getApprovalNotificationMessage(props.kind),
        ),
      );
    },
    onError(error, { projKey, envKey, flagKey }) {
      dispatch(requestApprovalRequestReviewFailed(projKey, envKey, flagKey, error));
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [APPROVAL_REQUESTS] });
    },
  });
}

export function useReviewApprovalRequest(props: {
  approvalRequestId: string;
  kind: ApprovalsReviewKind;
  projKey: string;
  envKey?: string;
  flagKey?: string;
}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<
    ApprovalRequest,
    ImmutableServerError,
    Omit<Parameters<typeof postReviewApprovalRequest>[0], 'kind'>
  >({
    mutationFn: (args) => postReviewApprovalRequest({ ...args, kind: props.kind }),
    mutationKey: [...approvalRequestQueryKeys.approvalRequestSummaryById(props.approvalRequestId), props.kind],
    onSuccess(approvalRequest) {
      if (props.flagKey) {
        dispatch(
          requestApprovalRequestReviewDone(
            props.projKey,
            props.envKey || '',
            props.flagKey,
            approvalRequest,
            getApprovalNotificationMessage(props.kind),
          ),
        );
      } else {
        toastQueue.add({ title: getApprovalNotificationMessage(props.kind), status: 'success' });
      }
    },
    onError(error) {
      if (props.flagKey) {
        dispatch(requestApprovalRequestReviewFailed(props.projKey, props.envKey || '', props.flagKey, error));
      } else {
        snackbarQueue.add({ description: getApprovalErrorNotificationMessage(props.kind), status: 'error' });
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [APPROVAL_REQUESTS] });
    },
  });
}

export function useApplyApprovalRequestForFlagConfiguration(props: { approvalRequestId: string }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<
    ApprovalRequest,
    ImmutableServerError,
    Parameters<typeof postApplyApprovalRequestForFlagConfiguration>[0]
  >({
    mutationFn: (variables) => postApplyApprovalRequestForFlagConfiguration(variables),
    mutationKey: approvalRequestQueryKeys.approvalRequestSummaryById(props.approvalRequestId),
    onSuccess: (updatedApproval, { projKey, flagKey, envKey }) => {
      void dispatch(forceFetchFlag(projKey, flagKey, envKey));
      void dispatch(fetchScheduledChangesForFlag({ flagKey, projKey, envKey }));

      dispatch(requestApplyApprovalRequestDone(projKey, flagKey, envKey, updatedApproval));
    },
    onError(error, { projKey, flagKey, envKey }) {
      dispatch(requestApplyApprovalRequestFailed(error, projKey, flagKey, envKey));
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [APPROVAL_REQUESTS] });
    },
  });
}

export function useApplyApprovalRequest(props: {
  approvalRequestId: string;
  projKey: string;
  envKey?: string;
  flagKey?: string;
}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<ApprovalRequest, ImmutableServerError, Parameters<typeof postApplyApprovalRequest>[0]>({
    mutationFn: (variables) => postApplyApprovalRequest(variables),
    mutationKey: approvalRequestQueryKeys.approvalRequestSummaryById(props.approvalRequestId),
    onSuccess: (updatedApproval) => {
      if (props.flagKey) {
        void dispatch(forceFetchFlag(props.projKey, props.flagKey, props.envKey || ''));
        void dispatch(
          fetchScheduledChangesForFlag({ flagKey: props.flagKey, projKey: props.projKey, envKey: props.envKey || '' }),
        );
        dispatch(requestApplyApprovalRequestDone(props.projKey, props.flagKey, props.envKey || '', updatedApproval));
      } else if (updatedApproval.status === ApprovalWorkflowExecutionStatus.FAILED) {
        snackbarQueue.add({ description: getApprovalAppliedNotificationMessage(updatedApproval), status: 'error' });
      } else {
        toastQueue.add({ title: getApprovalAppliedNotificationMessage(updatedApproval), status: 'success' });
      }
    },
    onError(error) {
      if (props.flagKey) {
        dispatch(requestApplyApprovalRequestFailed(error, props.projKey, props.flagKey, props.envKey || ''));
      } else {
        snackbarQueue.add({ description: getApprovalApplyFailedNotificationMessage(), status: 'error' });
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [APPROVAL_REQUESTS],
      });
    },
  });
}

export function useDeleteApprovalRequestForFlagConfiguration(props: { navigateTo: To }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<void, ImmutableServerError, Parameters<typeof deleteApprovalRequestForFlagConfiguration>[0]>({
    mutationFn: (variables) => deleteApprovalRequestForFlagConfiguration(variables),
    async onMutate() {
      // navigate before mutating so that we don't end up in a flashing "not found" state on the approval request details page
      await navigate(props.navigateTo);
    },
    onSuccess(_, { projKey, flagKey, envKey, requestId }) {
      dispatch(requestDeleteApprovalRequestDone(projKey, flagKey, envKey, requestId));
    },
    onError(error, { projKey, flagKey, envKey, requestId }) {
      dispatch(requestDeleteApprovalRequestFailed(error, projKey, flagKey, envKey, requestId));
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [APPROVAL_REQUESTS],
      });
    },
  });
}

export function useDeleteApprovalRequest(props: {
  navigateTo: To;
  projKey: string;
  envKey?: string;
  flagKey?: string;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<void, ImmutableServerError, Parameters<typeof deleteApprovalRequest>[0]>({
    mutationFn: (variables) => deleteApprovalRequest(variables),
    async onMutate() {
      // navigate before mutating so that we don't end up in a flashing "not found" state on the approval request details page
      await navigate(props.navigateTo);
    },
    onSuccess(_, { requestId }) {
      if (props.flagKey) {
        dispatch(requestDeleteApprovalRequestDone(props.projKey, props.flagKey, props.envKey || '', requestId));
      } else {
        toastQueue.add({ title: 'Approval request deleted', status: 'success' });
      }
    },
    onError(error, { requestId }) {
      if (props.flagKey) {
        dispatch(
          requestDeleteApprovalRequestFailed(error, props.projKey, props.flagKey, props.envKey || '', requestId),
        );
      } else {
        snackbarQueue.add({ description: 'Failed to delete approval request. Try again later.', status: 'error' });
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [APPROVAL_REQUESTS],
      });
    },
  });
}

export function useIsApprovalRequestUpdatePending(props: { approvalRequestId: string; kind?: ApprovalsReviewKind }) {
  const mutationKey = approvalRequestQueryKeys.approvalRequestSummaryById(props.approvalRequestId);
  return useIsMutating({ mutationKey: props.kind !== null ? [...mutationKey, props.kind] : mutationKey }) > 0;
}

export function useAddReviewersToApprovalRequestForFlagConfiguration(
  projKey: string,
  envKey: string,
  flagKey: string,
  requestId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { notifyMemberIds?: string[]; diffNumReviewers: number; notifyTeamKeys?: string[] }) =>
      updateReviewersForApprovalRequestForFlagConfiguration({
        projKey,
        envKey,
        flagKey,
        requestId,
        notifyMemberIds: variables.notifyMemberIds,
        notifyTeamKeys: variables.notifyTeamKeys,
      }),
    onSuccess(_, variables) {
      const successMessage = `${pluralize(
        variables.diffNumReviewers,
        'Reviewer',
        'Reviewers',
      )} successfully added to approval request`;
      toastQueue.add({ title: successMessage, status: 'success' });
    },
    onError() {
      toastQueue.add({ title: 'Unable to add reviewers at this time', status: 'error' });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [APPROVAL_REQUESTS],
      });
    },
  });
}

export function useAddReviewersToApprovalRequest(props: { approvalRequestId: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { notifyMemberIds?: string[]; diffNumReviewers: number; notifyTeamKeys?: string[] }) =>
      updateReviewersForApprovalRequest({
        requestId: props.approvalRequestId,
        notifyMemberIds: variables.notifyMemberIds,
        notifyTeamKeys: variables.notifyTeamKeys,
      }),
    onSuccess(_, variables) {
      const successMessage = `${pluralize(
        variables.diffNumReviewers,
        'Reviewer',
        'Reviewers',
      )} successfully added to approval request`;
      toastQueue.add({ title: successMessage, status: 'success' });
    },
    onError() {
      toastQueue.add({ title: 'Unable to add reviewers at this time', status: 'error' });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [APPROVAL_REQUESTS],
      });
    },
  });
}

/**
 * Expose a function to invalidate the approval request query cache when a mutation is successful.
 *
 * Approval request data fetching uses its own query cache, and attempts to be polymorphic on the
 * resource kind. Wherever you want to invalidate the approval request query cache, you can use this function.
 */
export function useInvalidateApprovalRequests() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: [APPROVAL_REQUESTS] });
  };
}

/**
 * Query options factory for fetching approval request summaries with arbitrary filters.
 * Use with useSuspenseQuery to leverage Suspense boundaries.
 */
export function getApprovalRequestSummariesQueryOptions(filters: ApprovalRequestSummariesFilters) {
  return {
    queryKey: approvalRequestQueryKeys.approvalRequestSummariesList(filters),
    queryFn: async (): Promise<ApprovalRequestSummariesListResponse> => {
      const response = await getApprovalRequestsForAccount({
        params: {
          ...makeApprovalRequestDashboardQueryParams(filters),
          expand: getApprovalRequestSummaryExpandProps(),
        },
      });

      const { approvalRequests } = response.entities;
      const { items } = response.result;
      return {
        ...response.result,
        items: (approvalRequests && items && items.map((id) => approvalRequests[id])) || [],
      };
    },
    ...approvalRequestQueryDefaultOptions,
    staleTime: 60 * 1000,
  };
}

/**
 * Query options factory for fetching pending experiment approval requests.
 * Use with useSuspenseQuery to leverage Suspense boundaries.
 */
export function getExperimentPendingApprovalQueryOptions({
  projectKey,
  environmentKey,
  experimentKey,
}: {
  projectKey: string;
  environmentKey: string;
  experimentKey: string;
}) {
  return getApprovalRequestSummariesQueryOptions({
    resourceId: `proj/${projectKey}:env/${environmentKey}:experiment/${experimentKey}`,
    status: [ApprovalRequestStatusEnum.PENDING, ApprovalRequestStatusEnum.APPROVED],
  });
}
